import React from 'react'

import { Box, Href, Text, useClassNames } from '@factorialco/deprecated-design-system'
import { ArrowCycle, FileFilled, MessageFilled } from '@factorialco/f0-react/icons/app'

import { convertDate } from 'modules/core/lib/dates'

import 'modules/complaints/pages/ComplaintDetailPage/ComplaintActivity/Feed/index.styles.css'

const stylesRefs = {
  element: 'Feed_element',
  lastElement: 'Feed_lastElement',
  iconContainer: 'Feed_iconContainer',
}

type ActivityType = 'message_company' | 'message_anonymous' | 'status_change' | 'default'

export interface ActivityItem {
  id: string
  title: string
  date: string
  text?: string
  files?: Array<{
    id: number
    filename: string
    path: string
  }>
  actor?: string
  action?: string
  isMessage?: boolean
  isStatusChange?: boolean
  type: ActivityType
}

interface ActivityElementProps {
  activity: ActivityItem
  isLast: boolean
  isFirst: boolean
}

const IconWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    background="white"
    borderRadius={{ all: 'abs010' }}
    border={{
      all: {
        color: 'grey400',
        width: 's1',
        style: 'solid',
      },
    }}
    width="s32"
    height="s32"
    justifyContent="center"
    alignItems="center"
  >
    {children}
  </Box>
)

const getIconComponent = (type: ActivityType, isFirst: boolean) => {
  const iconStyle = { width: '20px', height: '20px' }

  if (isFirst && (type === 'message_company' || type === 'message_anonymous')) {
    return <FileFilled style={iconStyle} />
  }

  switch (type) {
    case 'message_company':
    case 'message_anonymous':
      return <MessageFilled style={iconStyle} />
    case 'status_change':
      return <ArrowCycle style={iconStyle} />
    default:
      return <MessageFilled style={iconStyle} />
  }
}

const getActivityIcon = (type: ActivityType, isFirst: boolean): JSX.Element => {
  return <IconWrapper>{getIconComponent(type, isFirst)}</IconWrapper>
}

export const ActivityElement: React.FC<ActivityElementProps> = ({ activity, isLast, isFirst }) => {
  const cx = useClassNames(stylesRefs)

  const elementContent: React.ReactNode | null | undefined = (() => {
    if (!activity.isMessage && !activity.isStatusChange) return null
    if (activity.isStatusChange) return null

    return (
      <Box
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'abs012',
        }}
        padding="s16"
        gap="s8"
      >
        {activity.text && (
          <Text.Normal color="grey800" size="300">
            {activity.text}
          </Text.Normal>
        )}
        {activity.files && activity.files.length > 0 && (
          <Box gap="s8">
            {activity.files.map((file) => (
              <Href key={file.id} href={file.path} target="_blank" download={file.filename}>
                <Box flexDirection="row" gap="s8" alignItems="center">
                  <Box flexNoShrink>
                    <FileFilled style={{ width: '20px', height: '20px', color: '#0116379C' }} />
                  </Box>
                  <Text.Normal size="200" ellipsis color="grey800">
                    {file.filename}
                  </Text.Normal>
                </Box>
              </Href>
            ))}
          </Box>
        )}
      </Box>
    )
  })()

  const activityIcon = getActivityIcon(activity.type, isFirst)

  return (
    <Box
      flexDirection="row"
      gap="s16"
      className={cx('element', {
        lastElement: isLast,
      })}
    >
      <Box alignItems="center" flexNoShrink className={cx('iconContainer')}>
        {activityIcon}
      </Box>

      <Box gap="s4" flexGrow>
        <Text.Normal size="300" color="grey1000">
          {activity.actor && activity.action
            ? `${activity.actor} ${activity.action}`
            : activity.title}
        </Text.Normal>
        <Box paddingBottom="s12">
          <Text.Normal size="200" color="grey800">
            {convertDate(activity.date, 'backend_datetime', 'ui_short_with_time')}
          </Text.Normal>
        </Box>
        {elementContent}
      </Box>
    </Box>
  )
}
